import type {
    ApiConfig,
    ApiServiceConfig,
    ClassicSaljaPaths,
    OrderTakingBffPaths,
    OrderTakingPaths,
} from '@/config/types.config';

interface CreateApiConfigParams {
    classicSaljaBaseUrl: string;
    orderTakingBaseUrl: string;
    orderTakingBffBaseUrl: string;
    classicSaljaPathBaseUrls?: Partial<Record<keyof ClassicSaljaPaths, string>>;
    orderTakingPathBaseUrls?: Partial<Record<keyof OrderTakingPaths, string>>;
    orderTakingBffPathBaseUrls?: Partial<Record<keyof OrderTakingBffPaths, string>>;
}

const classicSaljaPaths: ClassicSaljaPaths = {
    deleteDraft: '/api/draft-orders/update-status',
    modifyOrder: '/api/order-modify',
    downloadAttachment: '/api/order-attachment/download',
    orderOutput: '/api/order-output',
    orderRetrieval: '/api/order-retrieval',
    draftRetrieval: '/api/draft-orders',
    storeDetails: '/api/stores',
    storePreferences: '/api/store-preferences/config',
    coworkerPreferences: '/api/coworker-privileges/preferences',
    uploadAttachment: '/api/order-attachment/upload',
    services: '/api/services',
    modifyOrderPaymentDate: '/api/order-modify/payment-date',
};

const orderTakingPaths: OrderTakingPaths = {
    sendMessage: '/api/v2/messaging/send-message',
    orderMessages: '/api/v2/order-messages',
    getAssets: '/api/v2/customer-assets/assets',
    getOrderDetails: '/api/v2/customer-assets/asset-details/order',
    getDraftDetails: '/api/v2/customer-assets/asset-details/draft',
    getQuotationDetails: '/api/v2/customer-assets/asset-details/quote',
    deleteQuotation: '/api/v2/quotation-manager/quotation',
    quotationRetrieval: '/api/v2/quotation-manager/quotation/by-id',
    coworker: '/api/v2/coworker',
    docusign: '/api/v2/docusign',
    deleteDraft: '/api/v2/draft-order-manager/draft-orders/update-status',
    draftRetrieval: '/api/v2/draft-order-manager/draft-orders',
    trpc: '/trpc',
};

const orderTakingBffPaths: OrderTakingBffPaths = {
    extraNeedsRequest: '/v1/enr',
};

const trimTrailingSlashes = (value: string): string => {
    let end = value.length;

    while (end > 0 && value[end - 1] === '/') {
        end -= 1;
    }

    return value.slice(0, end);
};

const ensureLeadingSlash = (value: string): string => (value.startsWith('/') ? value : `/${value}`);

const joinBaseUrl = (baseUrl: string, path: string): string => {
    return `${trimTrailingSlashes(baseUrl)}${ensureLeadingSlash(path)}`;
};

export const getServiceEndpoint = <Paths extends Record<keyof Paths, string>>(
    serviceConfig: ApiServiceConfig<Paths>,
    pathName: keyof Paths
): string => {
    return joinBaseUrl(getServiceBaseUrl(serviceConfig, pathName), serviceConfig.paths[pathName]);
};

export const getServiceBaseUrl = <Paths extends Record<keyof Paths, string>>(
    serviceConfig: ApiServiceConfig<Paths>,
    pathName?: keyof Paths
): string => {
    return trimTrailingSlashes(
        pathName ? (serviceConfig.pathBaseUrls?.[pathName] ?? serviceConfig.baseUrl) : serviceConfig.baseUrl
    );
};

export const createApiConfig = ({
    classicSaljaBaseUrl,
    orderTakingBaseUrl,
    orderTakingBffBaseUrl,
    classicSaljaPathBaseUrls,
    orderTakingPathBaseUrls,
    orderTakingBffPathBaseUrls,
}: CreateApiConfigParams): ApiConfig => {
    const apiConfig: ApiConfig = {
        services: {
            classicSalja: {
                baseUrl: trimTrailingSlashes(classicSaljaBaseUrl),
                paths: classicSaljaPaths,
                pathBaseUrls: classicSaljaPathBaseUrls,
            },
            orderTaking: {
                baseUrl: trimTrailingSlashes(orderTakingBaseUrl),
                paths: orderTakingPaths,
                pathBaseUrls: orderTakingPathBaseUrls,
            },
            orderTakingBff: {
                baseUrl: trimTrailingSlashes(orderTakingBffBaseUrl),
                paths: orderTakingBffPaths,
                pathBaseUrls: orderTakingBffPathBaseUrls,
            },
        },
    };

    return apiConfig;
};
